import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, viewChild } from '@angular/core';

import {
  BehaviorSubject,
  Subject, switchMap, take, takeUntil
} from 'rxjs';

import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  BenefitVerificationsHttpService,
  ClientInsuranceHttpService,
  CoverageType,
  IBenefitsInformation,
  PatientInsuranceStore
} from '@sondermind/data-access/client-insurance';
import { IrisButtonContainerAlignments, IrisButtonModule, IrisButtonTypes } from '@sondermind/iris/button';
import { SegmentService } from '@sondermind/segment';
import {
  CurrentUserSegmentEvents,
  ICurrentUserSegmentSchema
} from '@sondermind/data-access/client-user';
import { AccountSetupContext } from '@sondermind/constants';
import { InsuranceProgressStepperComponent } from './components/insurance-progress-stepper/insurance-progress-stepper.component';
import { CostEstimationFaqComponent } from './components/cost-estimation-faq/cost-estimation-faq.component';

const COVERAGE_TYPE = CoverageType;

@Component({
  selector: 'client-cost-estimation',
  templateUrl: './client-cost-estimation.component.html',
  styleUrls: ['./client-cost-estimation.component.scss'],
  imports: [
    CommonModule,
    MatProgressSpinnerModule,
    IrisButtonModule,
    InsuranceProgressStepperComponent,
    CostEstimationFaqComponent
  ]
})
export class ClientCostEstimationComponent implements OnInit, OnDestroy {
  @Input()
    accountSetupType!: AccountSetupContext | string;
  @Input() userHasSuccessfulMostRecentBv!: boolean;
  @Input() fromDirectBooking: boolean = false;
  @Output() costEstimationReviewed: EventEmitter<void> = new EventEmitter<void>();

  // Template for the action button that parent component can render
  readonly actionButtonTemplate = viewChild.required<TemplateRef<unknown>>('actionButton');

  IrisButtonContainerAlignments = IrisButtonContainerAlignments;
  IrisButtonTypes = IrisButtonTypes;

  private destroyedSubject = new Subject<void>();
  private destroyed$ = this.destroyedSubject.asObservable();

  isLoading$ = new BehaviorSubject(true);

  benefitsInfo!: IBenefitsInformation;
  sessionCovered: boolean = false;
  onlyPayCopay: boolean = false;
  showInsurancePlanStepper: boolean = false;

  insurancePayorName!: string;

  constructor(
    private clientInsuranceHttp: ClientInsuranceHttpService,
    private benefitVerificationsHttp: BenefitVerificationsHttpService,
    private patientInsuranceStore: PatientInsuranceStore,
    private segmentService: SegmentService<ICurrentUserSegmentSchema>
  ) {}

  ngOnInit(): void {
    if (!this.userHasSuccessfulMostRecentBv) {
      this.reviewedCostEstimation();
    } else {
      this.patientInsuranceStore.loadPatientInsuranceList$()
        .pipe(
          switchMap(() => this.patientInsuranceStore.patientInsuranceList$),
          take(1),
          switchMap((insuranceList) => {
            this.insurancePayorName = insuranceList?.[0]?.providerNetworkCompany?.name ?? '';
            const insuranceId = insuranceList?.[0]?.id;

            return this.clientInsuranceHttp.fetchLastBenefitInformation$(insuranceId);
          }),
          takeUntil(this.destroyed$)
        )
        .subscribe({
          next: (benefitInfoResp) => {
            this.benefitsInfo = benefitInfoResp;

            // Checks to see if the client's benefits structure is one of the types we want
            // to show the cost estimation for, decided by Product. Also checks to ensure that
            // the cost estimate value is not null or undefined.
            if (this.benefitsInfo?.benefitStructureConfig != null) {
              // If benefitStructureConfig exists, use its displayCostEstimate flag
              if (this.benefitsInfo.benefitStructureConfig.displayCostEstimate &&
                this.benefitsInfo?.displayedClientCostEstimate != null) {
                this.setPageComponents();
              } else {
                this.reviewedCostEstimation();
              }
            } else if (this.benefitsInfo?.displayCostEstimate &&
              this.benefitsInfo?.displayedClientCostEstimate != null &&
              this.supportedBenefitsStructures()) {
              // Fall back to existing logic if benefitStructureConfig is not present
              this.setPageComponents();
            } else {
              this.reviewedCostEstimation();
            }

            this.isLoading$.next(false);
          },
          error: () => {
            this.reviewedCostEstimation();
            this.isLoading$.next(false);
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.destroyedSubject.next();
    this.destroyedSubject.complete();
  }

  // Emits event to parent component to either skip the cost estimation page or the user is done viewing it
  reviewedCostEstimation(): void {
    this.costEstimationReviewed.emit();
  }

  // Set the UI page components based on the benefits structure
  setPageComponents(): void {
    if (this.benefitsInfo?.benefitStructure?.name === COVERAGE_TYPE.COPAY_NO_DEDUCTIBLE_NO_OOP_MAX) {
      this.sessionCovered = true;
      this.showInsurancePlanStepper = false;
      this.onlyPayCopay = true;
    } else if (this.benefitsInfo?.benefitStructure?.name === COVERAGE_TYPE.COPAY_NO_DEDUCTIBLE_WITH_OOP_MAX) {
      this.sessionCovered = true;
      this.showInsurancePlanStepper = true;
      this.onlyPayCopay = this.benefitsInfo?.displayedClientCostEstimate > 0;
    } else if (this.benefitsInfo?.benefitStructure?.name === COVERAGE_TYPE.COPAY_AFTER_DEDUCTIBLE_WITH_OOP_MAX) {
      this.sessionCovered = this.benefitsInfo?.indDeductibleRemaining.amount === 0;
      this.showInsurancePlanStepper = true;
      this.onlyPayCopay = this.benefitsInfo?.indDeductibleRemaining.amount === 0 && this.benefitsInfo?.indOutOfPocketRemaining.amount > 0;
    } else if (this.benefitsInfo?.benefitStructure?.name === COVERAGE_TYPE.COPAY_AFTER_DEDUCTIBLE_NO_OOP_MAX) {
      this.sessionCovered = this.benefitsInfo?.indDeductibleRemaining.amount === 0;
      this.showInsurancePlanStepper = true;
      this.onlyPayCopay = this.benefitsInfo?.indDeductibleRemaining.amount === 0;
    } else if (this.benefitsInfo?.benefitStructure?.name === COVERAGE_TYPE.COINSURANCE_AFTER_DEDUCTIBLE_WITH_OOP_MAX) {
      this.sessionCovered = this.benefitsInfo?.displayedClientCostEstimate === 0;
      this.showInsurancePlanStepper = true;
      this.onlyPayCopay = false;
    } else if (this.benefitsInfo?.benefitStructure?.name === COVERAGE_TYPE.COINSURANCE_AFTER_DEDUCTIBLE_NO_OOP_MAX) {
      this.sessionCovered = false;
      this.showInsurancePlanStepper = true;
      this.onlyPayCopay = false;
    } else if (this.benefitsInfo?.benefitStructure?.name === COVERAGE_TYPE.COINSURANCE_NO_DEDUCTIBLE_WITH_OOP_MAX) {
      this.sessionCovered = this.benefitsInfo?.displayedClientCostEstimate === 0;
      this.showInsurancePlanStepper = true;
      this.onlyPayCopay = false;
    } else if (this.benefitsInfo?.benefitStructure?.name === COVERAGE_TYPE.COINSURANCE_NO_DEDUCTIBLE_NO_OOP_MAX) {
      this.sessionCovered = false;
      this.showInsurancePlanStepper = false;
      this.onlyPayCopay = false;
    } else if (this.benefitsInfo?.benefitStructure?.name === COVERAGE_TYPE.COPAY_AND_COINSURANCE_NO_DEDUCTIBLE_WITH_OOP_MAX) {
      this.sessionCovered = this.benefitsInfo?.displayedClientCostEstimate === 0 && this.benefitsInfo?.indOutOfPocketRemaining.amount === 0;
      this.showInsurancePlanStepper = true;
      this.onlyPayCopay = false;
    } else if (this.benefitsInfo?.benefitStructure?.name === COVERAGE_TYPE.COPAY_AND_COINSURANCE_NO_DEDUCTIBLE_NO_OOP_MAX) {
      this.sessionCovered = false;
      this.showInsurancePlanStepper = false;
      this.onlyPayCopay = false;
    } else if (this.benefitsInfo?.benefitStructure?.name === COVERAGE_TYPE.COPAY_AND_COINSURANCE_AFTER_DEDUCTIBLE_WITH_OOP_MAX) {
      this.sessionCovered = this.benefitsInfo?.displayedClientCostEstimate === 0 && this.benefitsInfo?.indOutOfPocketRemaining.amount === 0;
      this.showInsurancePlanStepper = true;
      this.onlyPayCopay = false;
    } else if (this.benefitsInfo?.benefitStructure?.name === COVERAGE_TYPE.COPAY_AND_COINSURANCE_AFTER_DEDUCTIBLE_NO_OOP_MAX) {
      this.sessionCovered = false;
      this.showInsurancePlanStepper = true;
      this.onlyPayCopay = false;
    } else if (this.benefitsInfo?.benefitStructure?.name === COVERAGE_TYPE.FULLY_COVERED) {
      this.sessionCovered = true;
      this.showInsurancePlanStepper = false;
      this.onlyPayCopay = false;
    } else if (this.benefitsInfo?.benefitStructure?.name === COVERAGE_TYPE.FULLY_COVERED_AFTER_DEDUCTIBLE) {
      this.sessionCovered = this.benefitsInfo?.displayedClientCostEstimate === 0;
      this.showInsurancePlanStepper = true;
      this.onlyPayCopay = false;
    } else {
      this.reviewedCostEstimation();
      return;
    }

    this.markCostEstimateShown();
    this.sendToSegment();
  }

  // All the benefits structures/coverage types that will have a cost estimation being shown for them
  // Will add to this list until every coverage type is supported and released to users
  supportedBenefitsStructures(): boolean {
    return this.benefitsInfo?.benefitStructure?.name === COVERAGE_TYPE.COPAY_NO_DEDUCTIBLE_WITH_OOP_MAX ||
           this.benefitsInfo?.benefitStructure?.name === COVERAGE_TYPE.FULLY_COVERED;
  }

  // Send the cost estimate shown event to the backend with the account setup type
  markCostEstimateShown(): void {
    this.benefitVerificationsHttp.costEstimateShown$(this.benefitsInfo?.id, this.accountSetupType)
      .pipe(takeUntil(this.destroyed$))
      .subscribe();
  }

  // Segment event sent when the cost estimation is displayed
  sendToSegment(): void {
    this.segmentService.track<CurrentUserSegmentEvents.COST_ESTIMATE_DISPLAYED>(
      CurrentUserSegmentEvents.COST_ESTIMATE_DISPLAYED,
      {
        coverageType: this.benefitsInfo?.benefitStructure.name,
        payerName: this.insurancePayorName,
        costEstimate: this.benefitsInfo?.displayedClientCostEstimate
      }
    );
  }
}
